import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';

export default function TeamJoin() {
  const { gameId: urlGameId } = useParams();
  const navigate = useNavigate();
  const { game, isConnected, joinGame, error, clearError } = useSocket();

  const [gameCode, setGameCode] = useState(urlGameId || '');
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState<string[]>(['']);
  const [isJoining, setIsJoining] = useState(false);

  // Check if we successfully joined
  useEffect(() => {
    if (game && game.teams.length > 0) {
      const ourTeam = game.teams.find(t => t.name === teamName);
      if (ourTeam) {
        // Store team info and navigate to play screen
        localStorage.setItem('widm-team', JSON.stringify({
          gameId: game.id,
          teamId: ourTeam.id,
          teamName: ourTeam.name
        }));
        navigate(`/play/${game.id}/${ourTeam.id}`);
      }
    }
  }, [game, teamName, navigate]);

  const addPlayer = () => {
    if (players.length < 6) {
      setPlayers([...players, '']);
    }
  };

  const removePlayer = (index: number) => {
    if (players.length > 1) {
      setPlayers(players.filter((_, i) => i !== index));
    }
  };

  const updatePlayer = (index: number, name: string) => {
    const newPlayers = [...players];
    newPlayers[index] = name;
    setPlayers(newPlayers);
  };

  const handleJoin = () => {
    if (!gameCode.trim() || !teamName.trim()) return;
    
    const filledPlayers = players.filter(p => p.trim());
    if (filledPlayers.length === 0) return;
    
    setIsJoining(true);
    clearError();
    joinGame(gameCode.toUpperCase(), teamName, filledPlayers);
    
    // Reset joining state after timeout
    setTimeout(() => setIsJoining(false), 3000);
  };

  return (
    <div className="container flex-center" style={{ minHeight: '100vh' }}>
      <div className="card" style={{ width: '100%', maxWidth: '500px' }}>
        <h2 className="mb-2 text-center" style={{ color: 'var(--color-primary)' }}>
          👥 Doe Mee als Team
        </h2>
        
        {error && (
          <div 
            className="card mb-2 animate-shake"
            style={{ background: 'var(--color-error)', padding: '12px' }}
          >
            {error}
          </div>
        )}
        
        {/* Game Code */}
        <div className="mb-2">
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
            Spelcode
          </label>
          <input
            type="text"
            placeholder="Bijv. ABC123"
            value={gameCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameCode(e.target.value.toUpperCase())}
            style={{ textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 }}
            disabled={!!urlGameId}
          />
        </div>
        
        {/* Team Name */}
        <div className="mb-2">
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
            Teamnaam
          </label>
          <input
            type="text"
            placeholder="Bijv. De Mollenjagers"
            value={teamName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTeamName(e.target.value)}
          />
        </div>
        
        {/* Players */}
        <div className="mb-2">
          <div className="flex-between mb-1">
            <label style={{ fontWeight: 600 }}>Spelers (max 6)</label>
            {players.length < 6 && (
              <button 
                className="btn btn-secondary" 
                style={{ padding: '4px 12px', fontSize: '0.875rem' }}
                onClick={addPlayer}
              >
                + Speler
              </button>
            )}
          </div>
          
          <div className="flex gap-1" style={{ flexDirection: 'column' }}>
            {players.map((player, index) => (
              <div key={index} className="flex gap-1">
                <input
                  type="text"
                  placeholder={`Speler ${index + 1}`}
                  value={player}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlayer(index, e.target.value)}
                />
                {players.length > 1 && (
                  <button 
                    className="btn btn-secondary"
                    style={{ padding: '8px 12px' }}
                    onClick={() => removePlayer(index)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <button 
          className="btn btn-primary btn-large"
          style={{ width: '100%' }}
          onClick={handleJoin}
          disabled={
            isJoining || 
            !gameCode.trim() || 
            !teamName.trim() || 
            players.filter(p => p.trim()).length === 0 ||
            !isConnected
          }
        >
          {!isConnected ? 'Verbinden...' : isJoining ? 'Bezig...' : '🚀 Doe Mee!'}
        </button>
        
        <p className="text-muted mt-2 text-center" style={{ fontSize: '0.875rem' }}>
          {isConnected ? '🟢 Verbonden met server' : '🔴 Niet verbonden'}
        </p>
      </div>
    </div>
  );
}
